import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { KeyRound } from 'lucide-react';

export interface GsLoginScreenProps {
  /** Nom de l'app affiché en titre (ex: « Console UG »). */
  appName: string;
  /** Sous-titre (ex: « Connectez-vous avec votre compte @groupesignalisation.ca »). */
  message?: string;
  onLogin: () => void;
  /** Libellé du CTA (défaut : « Se connecter avec Microsoft »). */
  loginLabel?: string;
  /** Icône du container teinté (défaut : KeyRound). Ignorée si `logoSrc` est fourni. */
  icon?: LucideIcon;
  /** Logo light mode (remplace le container teinté + icône). */
  logoSrc?: string;
  /** Logo dark mode (défaut : réutilise `logoSrc` si absent). */
  logoSrcDark?: string;
}

/**
 * Écran de connexion standard GS (extrait du pattern ad-hoc Console UG —
 * BLITZ-SHELL-V2). Discipline DS : CTA slate (UNE action primaire neutre),
 * icône dans container teinté — la marque n'est PAS un bouton.
 */
export function GsLoginScreen({
  appName,
  message = 'Connectez-vous avec votre compte @groupesignalisation.ca',
  onLogin,
  loginLabel = 'Se connecter avec Microsoft',
  icon: Icon = KeyRound,
  logoSrc,
  logoSrcDark,
}: GsLoginScreenProps) {
  const logoDark = logoSrcDark ?? logoSrc;

  return (
    <div className="flex min-h-screen items-center justify-center bg-page dark:bg-slate-900">
      <div className="text-center">
        {logoSrc ? (
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center">
            <img src={logoSrc} alt="" className="h-16 w-16 object-contain dark:hidden" />
            {logoDark && (
              <img src={logoDark} alt="" className="hidden h-16 w-16 object-contain dark:block" />
            )}
          </div>
        ) : (
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 dark:bg-blue-950/30">
            <Icon size={32} className="text-blue-600 dark:text-blue-400" />
          </div>
        )}
        <h1 className="mb-2 text-xl font-semibold text-slate-800 dark:text-slate-100">{appName}</h1>
        <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">{message}</p>
        <button
          type="button"
          onClick={onLogin}
          className="inline-flex touch-manipulation items-center gap-2 rounded-lg bg-slate-800 px-6 py-3 text-body font-semibold text-white transition-colors hover:bg-slate-700 dark:bg-slate-600 dark:hover:bg-slate-500"
        >
          {loginLabel}
        </button>
      </div>
    </div>
  );
}
